import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { addDays, isBefore, isEqual, isWeekend } from 'date-fns'
import { format } from 'date-fns-tz'
import { useEffect, useRef } from 'react'
import {
  PATIENT_AVAILABILITY_DURATION_INPUT_NAME,
  PATIENT_AVAILABILITY_INPUT_NAME,
} from '../consts'
import { type ScreeningAvailbility, constructTimeSlots } from './helpers'

export const AvailabilitySelector = ({
  selectedSlots,
  setSelectedSlots,
}: {
  selectedSlots: ScreeningAvailbility[]
  setSelectedSlots: (slots: ScreeningAvailbility[]) => void
}) => {
  const today = new Date()
  // same day bookings wouldnt really work
  const oneDayFromNow = addDays(today, 1)

  const weekdaysToShow = Array.from({ length: 10 })
    .map((_, index) => addDays(oneDayFromNow, index))
    .filter((day) => !isWeekend(day))

  return (
    <div className='show-scrollbar mb-36 flex grid-cols-1 flex-col gap-4 overflow-y-scroll rounded-md border border-neutral200 p-4 md:mb-0 md:h-[350px]'>
      {/*  add a hidden input to capture the screening visit duration hours */}
      <input
        className='hidden'
        name={PATIENT_AVAILABILITY_DURATION_INPUT_NAME}
        onChange={() => {}} // required to avoid React warning
        tabIndex={-1}
        type='number'
        value={3} // The times are hardcoded to be 3 hours (9-12, 12-3, 3-6)
      />
      {weekdaysToShow.map((day) => (
        <DaySlots
          day={day}
          key={day.toString()}
          selectedSlots={selectedSlots}
          setSelectedSlots={setSelectedSlots}
          today={today}
        />
      ))}
    </div>
  )
}

const DaySlots = ({
  day,
  selectedSlots,
  setSelectedSlots,
  today,
}: {
  day: Date
  selectedSlots: ScreeningAvailbility[]
  setSelectedSlots: (slots: ScreeningAvailbility[]) => void
  today: Date
}) => {
  const formattedDay = format(day, 'EEEE, MMMM d')

  const isSlotSelected = (slot: ScreeningAvailbility) => {
    return Boolean(
      selectedSlots.find(
        (selectedSlot) =>
          isEqual(selectedSlot.startTimeISO8601, slot.startTimeISO8601) &&
          isEqual(selectedSlot.endTimeISO8601, slot.endTimeISO8601),
      ),
    )
  }

  const timeSlots = constructTimeSlots(day)

  return (
    <div className='flex w-full flex-col gap-2'>
      <Text className='text-neutral600' styleName='p-strong'>
        {formattedDay}
      </Text>
      <div className='flex flex-col gap-2 md:flex-row'>
        {/* Lets make 3 time slots for each day  */}
        {timeSlots.map((slot) => {
          const isPast = isBefore(slot.startTimeISO8601, today)
          const isSelected = isSlotSelected(slot)

          return (
            <TimeSlotButton
              isPast={isPast}
              isSelected={isSelected}
              key={`${slot.startTimeISO8601}-${slot.endTimeISO8601}`}
              onClick={() => {
                trackEvent(TrackingEvents.APPLY_PACKAGE_AVAILABILITY_CLICKED, {
                  type: isSelected ? 'unselected' : 'selected',
                  val: slot.startTimeISO8601.toString(),
                })
                if (isSelected) {
                  setSelectedSlots(
                    // unselect slot
                    selectedSlots.filter(
                      (selectedSlot) =>
                        !isEqual(
                          selectedSlot.startTimeISO8601,
                          slot.startTimeISO8601,
                        ),
                    ),
                  )
                } else {
                  setSelectedSlots([...selectedSlots, slot])
                }
              }}
              slot={slot}
            />
          )
        })}
      </div>
    </div>
  )
}

const TimeSlotButton = ({
  isPast,
  isSelected,
  onClick,
  slot,
}: {
  isPast: boolean
  isSelected: boolean
  onClick: () => void
  slot: ScreeningAvailbility
}) => {
  return (
    <button
      className={classMerge(
        `w-full rounded-md border border-[1.5] border-primary600 py-1 font-semibold text-primary600 md:hover:bg-primary600 md:hover:text-white`,
        isSelected &&
          'bg-primary100 text-primary600 md:hover:bg-primary200 md:hover:text-primary600',
        isPast && 'cursor-not-allowed opacity-50',
      )}
      disabled={isPast}
      onClick={onClick}
      type='button' // necessary because the default is submit otherwise
    >
      {isSelected && <HiddenInput slot={slot} />}
      {format(slot.startTimeISO8601, 'h a')}
      <span className='font-normal'>
        {' '}
        to {format(slot.endTimeISO8601, 'h a')}
      </span>
    </button>
  )
}
const HiddenInput = ({ slot }: { slot: ScreeningAvailbility }) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true })) // dispatch event so that the form listener knows when an input has changed
    }
    return () => {
      // fire the event on unmount as well
      const form = document.getElementById('apply-success-form')
      if (form) {
        form.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }
  }, [])

  return (
    <input
      className='hidden'
      name={PATIENT_AVAILABILITY_INPUT_NAME}
      onChange={() => {}} // handled by onclick in parent, but required to avoid React warning
      ref={inputRef}
      tabIndex={-1}
      type='datetime-local'
      value={format(slot.startTimeISO8601, "yyyy-MM-dd'T'HH:mm:ss.SSS")}
    />
  )
}
